import { AddingState, NO_PARENT, node_id, TreeData, TreeNode, TreeStoreType, useTreeStoreInstance } from "../model"
import { TreeExtraInputAction, TreeNoRowAction, TreePerRowAction } from "../config-schema"
import { useMutation } from "@tanstack/react-query"
import { dispatchDefaultActions } from "../../../../shared/action-buttons/action-dispatch"
import { DefaultAction } from "../../../../shared/action-buttons/action-button-schema"
import { ComponentNamespace, getPubsub, PublishedDict, publishWith, useNamespace } from "../../../../redux/pubsub"
import { resolveTemplateString, resolveTemplateStringAndLogErrors } from "../../../../helpers/resolve-template-string"
import { TreePlusPlusConfigOptions, useTreeConfig } from "../config"
import { Parser, DataFactory, type Quad } from "n3"
import { timestamp } from "../../../../helpers/PublishUtils"
import { dataUpdatePayloadFromN3Quads, Logging } from "../../../../shared/data-update/from-n3-quads"
import { queryClient } from "@/queryClient"
import { createUseSparqlQueryOptions } from "@/helpers/use-sparql-query"
import { requireNonNull } from "../../../../helpers/require-non-null"
import { toast } from "sonner"
import { type ExpectExtends } from "../../../../utils/typescript"
import { useMaybeWidgetId } from "../../../../helpers/widget"
import { useAppCtx, WistorBackendAppCtx } from "../../../../hooks/use-app-ctx"
import { mapRecord } from "../../../../utils/typescript"

type TreeJob = (
  | TreeNoRowJob
  | TreePerRowJob
  | TreeExtraInputJob
)

type WithNodeInput<A> = A & { input: NodeAction }

export type TreeNoRowJob = Exclude<TreeNoRowAction, {type: 'native'}>
{ // eslint-disable-line no-lone-blocks
  type X = TreeNoRowJob['type'];
  type Y = Exclude<TreeNoRowAction['type'], 'native'>
  type _ = ExpectExtends<X, Y> & ExpectExtends<Y, X> // eslint-disable-line @typescript-eslint/no-unused-vars
}

export type TreePerRowJob = WithNodeInput<Exclude<TreePerRowAction, {type: 'native'}>>
{ // eslint-disable-line no-lone-blocks
  type X = TreePerRowJob['type']
  type Y = Exclude<TreePerRowAction['type'], 'native'>
  type _ = ExpectExtends<X, Y> & ExpectExtends<Y, X> // eslint-disable-line @typescript-eslint/no-unused-vars
}

type DefaultExtraInputJob = Exclude<TreeExtraInputAction, {type: 'native'}>
export type TreeExtraInputJob = (
  |  Extract<DefaultExtraInputJob, {type: DefaultAction['type']}>
  |  Extract<TreeExtraInputAction, {type: 'cancel-add-node'}> & { input: AddingStateAction }
  |  Extract<TreeExtraInputAction, {type: 'send-add-node-request'}> & { input: SendAddNodeRequestAction }
  |  Extract<TreeExtraInputAction, {type: 'cancel-edit-node'}> & { input: NodeAction }
  |  Extract<TreeExtraInputAction, {type: 'send-edit-node-request'}> & { input: SendEditNodeRequestAction }
  |  Extract<TreeExtraInputAction, {type: 'send-delete-node-request'}> & { input: NodeAction }
  |  Extract<TreeExtraInputAction, {type: 'reparent-node'}> & { input: ReparentAction }
  |  Extract<TreeExtraInputAction, {type: 'move-node'}> & { input: MoveAction }
)
{ // eslint-disable-line no-lone-blocks
  type X = TreeExtraInputJob['type']
  type Y = Exclude<TreeExtraInputAction['type'], 'native'>
  type _ = ExpectExtends<X, Y> & ExpectExtends<Y, X> // eslint-disable-line @typescript-eslint/no-unused-vars
}

export type TreePerRowNativeJob = WithNodeInput<Extract<TreePerRowAction, {type: 'native'}>>
export type TreeNoRowNativeJob = Extract<TreeNoRowAction, {type: 'native'}>

export function splitNativeActions<A extends { type: string }>(actions: A[]) {
  return {
    native: actions.filter((a): a is Extract<A, { type: 'native' }> => a.type === 'native'),
    regular: actions.filter((a): a is Exclude<A, { type: 'native' }> => a.type !== 'native'),
  }
}

type ActionVars
  = { vars?: Record<string, string> }
type NodeAction
  = ActionVars & { node: TreeNode }
type AddingStateAction
  = ActionVars & { addingState: AddingState }
type SendEditNodeRequestAction
  = ActionVars & NodeAction & FormSubmissionAction
type SendAddNodeRequestAction
  = ActionVars & FormSubmissionAction & { addingStateId: string }
type ReparentAction
  = ActionVars & { node: TreeNode, parent: TreeNode }
type MoveAction
  = ActionVars
  & {
  node: TreeNode
  target: TreeNode
  reorder: "before" | "after"
}

type FormSubmissionAction = {
  formData: TreeNodeFormData
}
type TreeNodeFormData = {
  parentUri: string | null
  uri: string
  label: string
}


export function useDispatchTreeActionsMutation() {
  const treeStoreInstance = useTreeStoreInstance()
  const treeConfig = useTreeConfig()
  const namespace = useNamespace()
  const widgetId = useMaybeWidgetId()
  const appCtx = useAppCtx()
  const widgetCtx = {treeConfig, widgetId, store: treeStoreInstance.getState()}

  const noRow = useMutation({
    mutationFn: async (jobs: TreeNoRowJob[]) => {
      if (appCtx.type === 'sparql-only') throw new Error('not supported')
      for (const job of jobs) {
        await dispatchTreeAction({...widgetCtx, job, namespace, appCtx})
      }
    }
  })
  const perRow = useMutation({
    mutationFn: async (jobs: TreePerRowJob[]) => {
      if (appCtx.type === 'sparql-only') throw new Error('not supported')
      for (const job of jobs) {
        await dispatchTreeAction({...widgetCtx, job, namespace, appCtx})
      }
    }
  })

  return {noRow, perRow}
}

export function useDispatchTreeActionMutation() {
  const treeStoreInstance = useTreeStoreInstance()
  const treeConfig = useTreeConfig()
  const namespace = useNamespace()
  const widgetId = useMaybeWidgetId()
  const appCtx = useAppCtx()
  const widgetCtx = {treeConfig, widgetId, store: treeStoreInstance.getState()}

  return useMutation({
    mutationFn: async (job: TreeExtraInputJob) => {
      if (appCtx.type === 'sparql-only') throw new Error('not supported')
      await dispatchTreeAction({...widgetCtx, job, namespace, appCtx})
    }
  })
}

export function treeNodeExpressionVars(node: TreeNode, treeData: TreeData, overrides?: Record<string, string>): Record<string, string> {
  const item = treeData.itemsById[node.itemId]
  const parentUri = getParentUri(node, treeData)
  const index = getSiblingIndex(node, treeData)

  const optionalParentObj: Record<string, string> = parentUri
    ? { "node_parentUri": parentUri }
    : {}

  return {
    ...mapRecord(item.rowResults, (_, data) => data.value),
    ...optionalParentObj,
    "node_uri": item.uri,
    "node_label": item.label.text ?? "",
    "node_hasChildren": String(node.childrenIds.length > 0),
    "node_hasParent": String(Boolean(parentUri)),
    "node_index": String(index),
    ...overrides,
  } as const
}

export function treeNodeDraftExpressionVars(formData: TreeNodeFormData, existingNode: TreeNode | null, treeData: TreeData) {
  const nodes = Object.values(treeData.nodesById).filter(node => node.itemId === formData.uri)
  const hasChildren = nodes.length > 0 && nodes.some(n => n.childrenIds.length > 0)
  const getNewIndex = (() => {
    if (!formData.parentUri) return 0
    const firstParentNode = requireNonNull(Object.values(treeData.nodesById).find(node => node.itemId === formData.parentUri))
    return firstParentNode.childrenIds.length
  })
  const index = existingNode ? getSiblingIndex(existingNode, treeData) : getNewIndex()

  const optionalParentObj = formData.parentUri
    ? { "node_parentUri": formData.parentUri }
    : {}

  return {
    ...optionalParentObj,
    "node_uri": formData.uri,
    "node_label": formData.label,
    "node_hasChildren": hasChildren,
    "node_hasParent": String(Boolean(formData.parentUri)),
    "node_index": String(index)
  } as const
}

export function getParentUri(node: TreeNode, treeData: TreeData) {
  const parentNode = (node && node.parentId !== NO_PARENT)
    ? treeData.nodesById[node.parentId]
    : null
  const parentItem = parentNode != null
    ? treeData.itemsById[parentNode.itemId]
    : null
  return parentItem ? parentItem.uri : null
}

function getSiblingIndex(node: TreeNode, treeData: TreeData) {
  return node.parentId === NO_PARENT
    ? 0
    : treeData.nodesById[node.parentId].childrenIds.indexOf(node.id)
}

export type TreeEditingUserData = {
  onSuccess?: DefaultAction[] | null
  onUriRenamed?: DefaultAction[] | null
}

async function dispatchTreeAction({treeConfig, store, job, namespace, widgetId, appCtx}: {
  treeConfig: TreePlusPlusConfigOptions,
  store: TreeStoreType,
  job: TreeJob,
  namespace: ComponentNamespace,
  widgetId: string | null,
  appCtx: WistorBackendAppCtx,}
) {
  const dispatchDefaultActionsAfter = async (actions: DefaultAction[]) => {
    for (const action of actions) {
      const vars = anyJobVars(job, store.treeData, namespace)
      await dispatchDefaultActions(action, appCtx.urlSparql, namespace, sourceId(treeConfig, widgetId), appCtx, vars)
    }
  }

  switch (job.type) {

    case "start-edit-node": {
      const vars = {
        ...getPubsub(namespace) as Record<string, string>,
        ...treeNodeExpressionVars(job.input.node, store.treeData),
        ...(job.input.vars ?? {}),
      }
      try {
        store.actions.startNodeEditing(job.input.node.id, {
          onSuccess: job.onSuccess,
          onUriRenamed: job.onUriRenamed
        } satisfies TreeEditingUserData)

        return toastIfResolved(vars, job.notification?.onSuccess)
      } catch (e) {
        toastIfResolved(vars, job.notification?.onFailure)
        throw e
      }
    }

    case "start-add-root-node": {
      const vars = getPubsub(namespace) as Record<string, string>
      try {
        store.actions.startAdding(NO_PARENT, {
          onSuccess: job.onSuccess
        } satisfies TreeEditingUserData)
        return toastIfResolved(vars, job.notification?.onSuccess)
      } catch (e) {
        toastIfResolved(vars, job.notification?.onFailure)
        throw e
      }
    }

    case "start-add-node": {
      const vars = {
        ...getPubsub(namespace) as Record<string, string>,
        ...treeNodeExpressionVars(job.input.node, store.treeData),
        ...('input' in job ? job.input.vars : {}),
      }
      let parent: undefined | string
      if (job.parent) {
        parent = resolveTemplateString(job.parent, vars)
        if (parent && !(parent in store.treeData.itemsById)) {
          toastIfResolved(vars, job.notification?.onFailure)
          throw new Error("parent ID does not exist in tree: " + parent)
        }
      }

      try {
        store.actions.startAdding(parent || NO_PARENT, {
          onSuccess: job.onSuccess
        } satisfies TreeEditingUserData)
        return toastIfResolved(vars, job.notification?.onSuccess)
      } catch (e) {
        toastIfResolved(vars, job.notification?.onFailure)
        throw e
      }
    }

    case "cancel-edit-node": {
      const vars = {
        ...getPubsub(namespace) as Record<string, string>,
        ...treeNodeExpressionVars(job.input.node, store.treeData),
        ...(job.input.vars ?? {})
      }
      try {
        store.actions.stopNodeEditing(job.input.node.id)
        return toastIfResolved(vars, job.notification?.onSuccess)
      } catch (e) {
        toastIfResolved(vars, job.notification?.onFailure)
        throw e
      }
    }

    case "cancel-add-node": {
      const vars = {
        ...getPubsub(namespace) as Record<string, string>,
        ...(job.input.vars ?? {})
      }
      try {
        store.actions.stopAdding(job.input.addingState.addingStateId)
        return toastIfResolved(vars, job.notification?.onSuccess)
      } catch (e) {
        toastIfResolved(vars, job.notification?.onFailure)
        throw e
      }
    }

    case "send-edit-node-request": {
      const vars = {
        ...getPubsub(namespace) as Record<string, string>,
        ...treeNodeExpressionVars(job.input.node, store.treeData),
        ...(job.input.vars ?? {})
      }
      try {
        await sendEditRequest(treeConfig, store.treeData, job.input, namespace, widgetId, appCtx)
        store.actions.stopNodeEditing(job.input.node.id)

        const isUriRenamed = job.input.node.itemId !== job.input.formData.uri
        if (job.onUriRenamed && isUriRenamed) {
          await dispatchDefaultActionsAfter(job.onUriRenamed)
        }
        if (job.onSuccess) {
          await dispatchDefaultActionsAfter(job.onSuccess)
        }
        return toastIfResolved(vars, job.notification?.onSuccess)

      } catch (e) {
        toastIfResolved(vars, job.notification?.onFailure)
        throw e
      }
    }

    case "send-add-node-request": {

      const vars = {
        ...getPubsub(namespace) as Record<string, string>,
        ...(job.input.vars ?? {})
      }
      try {

        await sendAddRequest(treeConfig, store.treeData, job.input, namespace, widgetId, appCtx)
        store.actions.stopAdding(job.input.addingStateId)
        if (!treeConfig.disableAutoItemSelectionAfterCreation) {
          store.actions.selectOnceWhenInTreeData(job.input.formData.uri)
        }
        if (job.onSuccess) {
          await dispatchDefaultActionsAfter(job.onSuccess)
        }
        return toastIfResolved(vars, job.notification?.onSuccess)
      } catch (e) {
        toastIfResolved(vars, job.notification?.onFailure)
        throw e
      }
    }

    case "reparent-node": {
      const vars = {
        ...getPubsub(namespace) as Record<string, string>,
        ...reparentNodeVars(job, store.treeData),
        ...treeNodeExpressionVars(job.input.node, store.treeData),
        ...(job.input.vars ?? {}),
      }
      try {

        await sendReparentRequest(treeConfig, store.treeData, job.input, namespace, widgetId, appCtx)
        if (job.onSuccess) {
          await dispatchDefaultActionsAfter(job.onSuccess)
        }
        return toastIfResolved(vars, job.notification?.onSuccess)

      } catch (e) {
        toastIfResolved(vars, job.notification?.onFailure)
        // TODO: errors get swallowed
        console.error(e)
        throw e
      }
    }

    case "move-node": {
      const vars = {
        ...getPubsub(namespace) as Record<string, string>,
        ...moveNodeVars(job, store.treeData),
        ...treeNodeExpressionVars(job.input.node, store.treeData),
        ...(job.input.vars ?? {}),
      }
      try {

        await sendMoveRequest(treeConfig, store.treeData, job.input, namespace, widgetId, appCtx)
        const sourceParentUri = getParentUri(job.input.node, store.treeData)
        const targetParentUri = getParentUri(job.input.target, store.treeData)
        if (sourceParentUri !== targetParentUri) {
          if (job.onReparented) {
            await dispatchDefaultActionsAfter(job.onReparented)
          }
        }
        if (job.onReordered) {
          await dispatchDefaultActionsAfter(job.onReordered)
        }
        return toastIfResolved(vars, job.notification?.onSuccess)

      } catch (e) {
        toastIfResolved(vars, job.notification?.onFailure)
        // TODO: errors get swallowed
        console.error(e)
        throw e
      }
    }

    case "send-delete-node-request": {
      const vars = {
        ...getPubsub(namespace) as Record<string, string>,
        ...treeNodeExpressionVars(job.input.node, store.treeData),
        ...(job.input.vars ?? {})
      }
      try {

        await sendDeleteRequest(treeConfig, store.treeData, job.input, namespace, widgetId, appCtx)
        if (job.onSuccess) {
          await dispatchDefaultActionsAfter(job.onSuccess)
        }
        return toastIfResolved(vars, job.notification?.onSuccess)

      } catch (e) {

        toastIfResolved(vars, job.notification?.onFailure)
        throw e

      }
    }
    default: {
      job.type satisfies DefaultAction['type']

      const vars = anyJobVars(job, store.treeData, namespace)
      await dispatchDefaultActions(job as DefaultAction, appCtx.urlSparql, namespace, sourceId(treeConfig, widgetId), appCtx, vars)
      return
    }
  }
}

/** This should only be used when you really cannot guarantee what comes out */
function anyJobVars(job: TreeJob, treeData: TreeData, namespace: ComponentNamespace) {
  const nodeVars = "input" in job && "node" in job.input
    ? treeNodeExpressionVars(job.input.node, treeData)
    : undefined
  const parentVars = job.type === 'reparent-node'
    ? reparentNodeVars(job, treeData) : undefined
  const reorderVars = job.type === 'move-node'
    ? moveNodeVars(job, treeData) : undefined
  const inputVars = 'input' in job ? job.input?.vars : undefined

  return {
    ...getPubsub(namespace) as Record<string, string>,
    ...(nodeVars ?? {}),
    ...(parentVars ?? {}),
    ...(reorderVars ?? {}),
    ...(inputVars ?? {})
  }
}

function reparentNodeVars(job: Extract<TreeJob, {type: 'reparent-node'}>, treeData: TreeData) {
  return {
    node_prevParentUri: getParentUri(job.input.node, treeData) ?? "",
    node_nextParentUri: treeData.itemsById[job.input.parent.itemId].uri
  }
}

function moveNodeVars(job: Extract<TreeJob, {type: 'move-node'}>, treeData: TreeData) {
  return {
    reorder: job.input.reorder,
    targetNode_uri: treeData.itemsById[job.input.target.itemId].uri,
    node_prevParentUri: getParentUri(job.input.node, treeData) ?? "",
    node_nextParentUri: getParentUri(job.input.target, treeData) ?? "",
  }
}


function toastIfResolved(vars: PublishedDict, messageTemplate?: string) {
  if (!messageTemplate) return
  const message = resolveTemplateStringAndLogErrors(messageTemplate, vars)
  if (message) toast(message)
}

async function sendAddRequest(
  treeConfig: TreePlusPlusConfigOptions,
  treeData: TreeData,
  input: FormSubmissionAction,
  namespace: ComponentNamespace,
  widgetId: string | null,
  appCtx: WistorBackendAppCtx,
) {
  const templateN3 = treeConfig.editTriplesTemplate
  if (!templateN3 || !templateN3.trim()) {
    throw new Error("Tree++ edit triples template has not been configured yet")
  }

  const templateVars = {
    ...getPubsub(namespace) ?? {},
    ...NO_PARENT_PLACEHOLDER_VAR,
    ...treeNodeDraftExpressionVars(input.formData, null, treeData)
  } as const

  const quads = resolveTrigTemplateToQuads(templateN3, templateVars)

  const graphs = new Set<string>()
  quads.forEach(q => graphs.add(q.graph.value))
  await verifyUriIsUnique(treeConfig, input.formData.uri, graphs, namespace)

  const dataPayload = dataUpdatePayloadFromN3Quads({ deletes: [], updates: quads, logging: loggingFrom(treeConfig) })
  await appCtx.requestDataUpdate(dataPayload)
  const actionId = `${sourceId(treeConfig, widgetId)} -- tree++ send add request`
  timestamp(publishWith(namespace, actionId), treeConfig.timestampParameters, templateVars)
}

async function sendEditRequest(
  treeConfig: TreePlusPlusConfigOptions,
  treeData: TreeData,
  input: SendEditNodeRequestAction,
  namespace: ComponentNamespace,
  widgetId: string | null,
  appCtx: WistorBackendAppCtx,
) {
  const templateN3 = treeConfig.editTriplesTemplate
  if (!templateN3 || !templateN3.trim()) {
    throw new Error("Tree++ node triples template has not been configured yet")
  }

  const updateTemplateVars = {
    ...getPubsub(namespace) ?? {},
    ...NO_PARENT_PLACEHOLDER_VAR,
    ...treeNodeDraftExpressionVars(input.formData, input.node, treeData)
  } as const
  const deleteTemplateVars = {
    ...getPubsub(namespace) ?? {},
    ...NO_PARENT_PLACEHOLDER_VAR,
    ...treeNodeExpressionVars(input.node, treeData)
  }

  const updateQuads = resolveTrigTemplateToQuads(templateN3, updateTemplateVars)
  const deleteQuads = resolveTrigTemplateToQuads(templateN3, deleteTemplateVars)
  const dataPayload = dataUpdatePayloadFromN3Quads({ updates: updateQuads, deletes: deleteQuads, logging: loggingFrom(treeConfig) })
  await appCtx.requestDataUpdate(dataPayload)
  const actionId = `${sourceId(treeConfig, widgetId)} -- tree++ send add request`
  timestamp(publishWith(namespace, actionId), treeConfig.timestampParameters, updateTemplateVars)
}

async function sendDeleteRequest(
  treeConfig: TreePlusPlusConfigOptions,
  treeData: TreeData,
  input: NodeAction,
  namespace: ComponentNamespace,
  widgetId: string | null,
  appCtx: WistorBackendAppCtx,
) {
  const templateN3 = treeConfig.editTriplesTemplate
  if (!templateN3 || !templateN3.trim()) {
    throw new Error("Tree++ edit triples template has not been configured yet")
  }

  const templateVars = {
    ...NO_PARENT_PLACEHOLDER_VAR,
    ...getPubsub(namespace) ?? {},
    ...treeNodeExpressionVars(input.node, treeData)
  } as const

  const quads = resolveTrigTemplateToQuads(templateN3, templateVars)
  const dataPayload = dataUpdatePayloadFromN3Quads({ updates: [], deletes: quads, logging: loggingFrom(treeConfig) })
  await appCtx.requestDataUpdate(dataPayload)

  const actionId = `${sourceId(treeConfig, widgetId)} -- tree++ send add request`
  timestamp(publishWith(namespace, actionId), treeConfig.timestampParameters, templateVars)
}

async function sendReparentRequest(
  treeConfig: TreePlusPlusConfigOptions,
  treeData: TreeData,
  input: ReparentAction,
  namespace: ComponentNamespace,
  widgetId: string | null,
  appCtx: WistorBackendAppCtx,
) {
  const templateN3 = treeConfig.editTriplesTemplate
  if (!templateN3 || !templateN3.trim()) {
    throw new Error("Tree++ edit triples template has not been configured yet")
  }

  const nextParentUri = treeData.itemsById[input.parent.itemId].uri
  const updateTemplateVars = {
    ...NO_PARENT_PLACEHOLDER_VAR,
    ...getPubsub(namespace) ?? {},
    ...treeNodeExpressionVars(input.node, treeData, {
      "node_parentUri": nextParentUri ?? "",
      "node_hasParent": String(Boolean(nextParentUri))
    }),
  } as const
  const updateQuads = resolveTrigTemplateToQuads(templateN3, updateTemplateVars)

  const deleteTemplateVars = {
    ...NO_PARENT_PLACEHOLDER_VAR,
    ...getPubsub(namespace) ?? {},
    ...treeNodeExpressionVars(input.node, treeData),
  } as const
  const deleteQuads = resolveTrigTemplateToQuads(templateN3, deleteTemplateVars)

  const dataPayload = dataUpdatePayloadFromN3Quads({ updates: updateQuads, deletes: deleteQuads, logging: loggingFrom(treeConfig) })
  await appCtx.requestDataUpdate(dataPayload)

  const actionId = `${sourceId(treeConfig, widgetId)} -- tree++ send add request`
  timestamp(publishWith(namespace, actionId), treeConfig.timestampParameters, updateTemplateVars)
}

type MoveRequestVars = {
  node_parentUri?: string
  node_hasParent?: string
  node_index?: string
}
type MoveRequestVarsPair = { updates: MoveRequestVars, deletes: MoveRequestVars }
type MoveRequestVarsRecord = Record<string, MoveRequestVarsPair>

async function sendMoveRequest(
  treeConfig: TreePlusPlusConfigOptions,
  treeData: TreeData,
  input: MoveAction,
  namespace: ComponentNamespace,
  widgetId: string | null,
  appCtx: WistorBackendAppCtx,
) {
  const { node, target, reorder } = input

  const oldParentId = node.parentId
  const newParentId = target.parentId
  const oldParent = oldParentId !== NO_PARENT ? treeData.nodesById[oldParentId] : NO_PARENT
  const newParent = newParentId !== NO_PARENT ? treeData.nodesById[newParentId] : NO_PARENT

  const templateN3 = treeConfig.editTriplesTemplate
  if (!templateN3 || !templateN3.trim()) {
    throw new Error("Tree++ edit triples template has not been configured yet")
  }

  const varsByNodeId: MoveRequestVarsRecord = {}

  // Build final sibling lists
  const oldSiblings = [...getSiblingIds(treeData, oldParentId)]
  const newSiblings =
    oldParentId === newParentId
      ? oldSiblings
      : [...getSiblingIds(treeData, newParentId)]
  const newSiblingsBeforeAdding = [ ...newSiblings ]

  const oldIndex = oldSiblings.indexOf(node.id)
  if (oldIndex !== -1) oldSiblings.splice(oldIndex, 1)

  const targetIndex = newSiblings.indexOf(target.id)
  const insertIndex = reorder === "before" ? targetIndex : targetIndex + 1
  newSiblings.splice(insertIndex, 0, node.id)

  // Reparent node if needed
  if (oldParent !== newParent) {
    const nodeVars: MoveRequestVarsPair = { updates: {}, deletes: {}}
    varsByNodeId[node.id] = nodeVars

    const nextParentUri = newParent === NO_PARENT
      ? null
      : treeData.itemsById[newParent.itemId].uri

    const currentVars = treeNodeExpressionVars(node, treeData)
    nodeVars.deletes.node_parentUri = currentVars.node_parentUri ?? NO_PARENT_PLACEHOLDER_STR
    nodeVars.deletes.node_hasParent = currentVars.node_hasParent
    nodeVars.updates.node_parentUri = nextParentUri ?? NO_PARENT_PLACEHOLDER_STR
    nodeVars.updates.node_hasParent = String(Boolean(nextParentUri))
  }

  // Reindex new parent siblings
  for (let index = 0; index < newSiblings.length; index++) {
    const siblingId = newSiblings[index]

    if (!(siblingId in varsByNodeId)) {
      varsByNodeId[siblingId] = { updates: {}, deletes: {} }
    }

    const siblingVars = varsByNodeId[siblingId]
    siblingVars.deletes.node_index = String(newSiblingsBeforeAdding.indexOf(newSiblings[index]))
    siblingVars.updates.node_index = String(index)
  }

  const allUpdateQuads: Quad[] = []
  const allDeleteQuads: Quad[] = []
  for (const [ nodeId, pair ] of Object.entries(varsByNodeId)) {
    const node = treeData.nodesById[nodeId]
    const deletes = {
      ...NO_PARENT_PLACEHOLDER_VAR,
      ...getPubsub(namespace),
      ...treeNodeExpressionVars(node, treeData, pair.deletes)
    }
    const updates = {
      ...NO_PARENT_PLACEHOLDER_VAR,
      ...getPubsub(namespace),
      ...treeNodeExpressionVars(node, treeData, pair.updates)
    }
    resolveTrigTemplateToQuads(templateN3, deletes)
      .forEach(q => allDeleteQuads.push(q))
    resolveTrigTemplateToQuads(templateN3, updates)
      .forEach(q => allUpdateQuads.push(q))
  }

  const dataPayload = dataUpdatePayloadFromN3Quads({
    updates: allUpdateQuads,
    deletes: allDeleteQuads,
    logging: loggingFrom(treeConfig),
  })
  await appCtx.requestDataUpdate(dataPayload)

  const actionId = `${sourceId(treeConfig, widgetId)} -- tree++ send add request`
  timestamp(publishWith(namespace, actionId), treeConfig.timestampParameters, {})
}

function getSiblingIds(treeData: TreeData, parentId: node_id | typeof NO_PARENT) {
  return parentId === NO_PARENT
    ? treeData.rootNodeIds
    : treeData.nodesById[parentId].childrenIds
}

async function verifyUriIsUnique(
  treeConfig: TreePlusPlusConfigOptions,
  uri: string,
  graphs: Set<string>, namespace: ComponentNamespace,
) {
  for (const graph of graphs) {
    const res = await sparqlQuery(treeConfig.sparqlEndpoint,`
      SELECT (COUNT(*) as ?cnt) FROM <${graph}> WHERE {
        <${uri}> ?p ?o .
      }
    `, namespace)
    const count = parseInt(res.results.bindings?.[0]?.cnt?.value)
    if (isNaN(count)) throw new Error("Could not check if URI is unique, SPARQL result parse error")
    if (count !== 0) {
      throw new Error(`Provided URI <${uri}> already exists in graph <${graph}>`)
    }
  }
}

function sparqlQuery(endpoint: string, queryString: string, namespace: ComponentNamespace) {
  return queryClient.fetchQuery(createUseSparqlQueryOptions({
    query: queryString,
    sparqlEndpoint: endpoint,
    source: "tree-action-dispatch",
    namespace,
  }))
}


const NO_PARENT_PLACEHOLDER_STR = 'wistor-internals::invalid-uri-placeholder::no-parent' as const
const NO_PARENT_PLACEHOLDER_VAR = {node_parentUri: NO_PARENT_PLACEHOLDER_STR} as const
const NO_PARENT_PLACEHOLDER_NODE = DataFactory.namedNode(NO_PARENT_PLACEHOLDER_STR)

function resolveTrigTemplateToQuads(templateN3: string, vars: PublishedDict): Quad[] {
  const resolvedN3 = resolveTemplateString(templateN3, vars)
  if (!resolvedN3) {
    console.error("Tree++ template that was not resolved", { templateN3, vars })
    throw new Error("Tree++ edit triples template resolution problem: most likely there are empty variables. See console for more details.")
  }

  const parser = new Parser()
  return parser.parse(resolvedN3).filter(containsNoPlaceholder)
}

function containsNoPlaceholder(quad: Quad) {
  return !quad.subject.equals(NO_PARENT_PLACEHOLDER_NODE)
    && !quad.object.equals(NO_PARENT_PLACEHOLDER_NODE)
}

function sourceId(treeConfig: TreePlusPlusConfigOptions, widgetId: string | null): string {
  return treeConfig.id ?? widgetId ?? "unknown"
}

function loggingFrom(config: TreePlusPlusConfigOptions): undefined | Logging {
  if (!config.mutationLogsEnabled) return undefined
  if (!config.mutationLogsGraph?.length) throw new Error('"graph to write edit-logs in" needs to be configured')
  return {
    graph: config.mutationLogsGraph,
    customStatements: config.mutationLogsCustomStatements ?? '',
  }
}
